import { BoardScalingMetrics, GameLayoutConfig } from '../services/game-metrics-service';

export type PositionData = {
  x: number;
  y: number;
};

export type RectData = {
  pos: PositionData;
  width: number;
  height: number;
};

export class Positioning {
  public static globalDeckCardRect(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): RectData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;
    const scaledOffsetX = scalingMetrics?.scaledOffsetX ?? 0;

    const cardWidth = layoutConfig.cardWidth;
    const cardHeight = layoutConfig.cardHeight;

    const xPos = cardWidth * 0.5 + layoutConfig.paddingLg;
    const yPos = layoutConfig.boardHeight * 0.11;

    return {
      pos: {
        x: xPos * scaleFactor + scaledOffsetX,
        y: yPos * scaleFactor,
      },
      width: cardWidth * scaleFactor,
      height: cardHeight * scaleFactor,
    };
  }

  public static globalCurrentCardRect(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): RectData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;
    const scaledOffsetX = scalingMetrics?.scaledOffsetX ?? 0;

    const cardWidth = layoutConfig.cardWidth;
    const cardHeight = layoutConfig.cardHeight;

    const xPos = layoutConfig.boardWidth * 0.5;
    const yPos = layoutConfig.boardHeight * 0.14;

    return {
      pos: {
        x: xPos * scaleFactor + scaledOffsetX,
        y: yPos * scaleFactor,
      },
      width: cardWidth * scaleFactor,
      height: cardHeight * scaleFactor,
    };
  }

  public static globalColumnRect(
    columnIndex: number,
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): RectData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;
    const scaledOffsetX = scalingMetrics?.scaledOffsetX ?? 0;

    const columnWidth = layoutConfig.columnWidth;
    const columnHeight = layoutConfig.columnHeight;

    const xPos =
      layoutConfig.boardWidth * 0.5 +
      (columnWidth + layoutConfig.paddingLg) * columnIndex +
      (columnWidth + layoutConfig.paddingLg) * 0.5;
    const yPos = layoutConfig.boardHeight * 0.63;

    return {
      pos: {
        x: xPos * scaleFactor + scaledOffsetX,
        y: yPos * scaleFactor,
      },
      width: columnWidth * scaleFactor,
      height: columnHeight * scaleFactor,
    };
  }

  public static localColumnStackRect(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): RectData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;

    const columnWidth = layoutConfig.columnWidth;
    const spacing = layoutConfig.columnSpacing;
    const columnHeight = layoutConfig.columnHeight;
    const columnHintHeight = layoutConfig.columnHintHeight;
    const columnScoreHeight = layoutConfig.columnScoreHeight;
    const columnStackHeight = columnHeight - columnHintHeight - columnScoreHeight - spacing;

    const xPos = 0;
    const yPos = (columnHeight - columnStackHeight) * 0.5;

    return {
      pos: {
        x: xPos * scaleFactor,
        y: yPos * scaleFactor,
      },
      width: columnWidth * scaleFactor,
      height: columnStackHeight * scaleFactor,
    };
  }

  public static localColumnHintRect(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): RectData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;

    const columnWidth = layoutConfig.columnWidth;
    const columnHeight = layoutConfig.columnHeight;
    const columnHintHeight = layoutConfig.columnHintHeight;

    const xPos = 0;
    const yPos = (columnHintHeight - columnHeight) * 0.5;

    return {
      pos: {
        x: xPos * scaleFactor,
        y: yPos * scaleFactor,
      },
      width: columnWidth * scaleFactor,
      height: columnHintHeight * scaleFactor,
    };
  }

  public static localColumnScoreRect(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): RectData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;

    const columnWidth = layoutConfig.columnWidth;
    const columnHeight = layoutConfig.columnHeight;
    const columnHintHeight = layoutConfig.columnHintHeight;
    const columnScoreHeight = layoutConfig.columnScoreHeight;

    const xPos = 0;
    const yPos = (columnScoreHeight - columnHeight) * 0.5 + columnHintHeight;

    return {
      pos: {
        x: xPos * scaleFactor,
        y: yPos * scaleFactor,
      },
      width: columnWidth * scaleFactor,
      height: columnScoreHeight * scaleFactor,
    };
  }

  public static localCardStackPosition(
    cardIndex: number,
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): PositionData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;

    const spacing = layoutConfig.columnSpacing;
    const columnHeight = layoutConfig.columnHeight;
    const columnHintHeight = layoutConfig.columnHintHeight;
    const columnScoreHeight = layoutConfig.columnScoreHeight;
    const columnStackHeight = columnHeight - (columnHintHeight + columnScoreHeight + spacing);
    const cardHeight = layoutConfig.cardHeight;

    const yCardOffset =
      columnStackHeight * -0.5 +
      cardHeight * 0.5 +
      cardIndex * layoutConfig.cardStackSpacing +
      layoutConfig.paddingSm;

    return { x: 0, y: yCardOffset * scaleFactor };
  }

  public static globalDeckPosition(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): PositionData {
    return Positioning.globalDeckCardRect(layoutConfig, scalingMetrics).pos;
  }

  public static globalCenterPosition(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): PositionData {
    return Positioning.globalCurrentCardRect(layoutConfig, scalingMetrics).pos;
  }

  public static globalColumnPosition(
    columnIndex: number,
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): PositionData {
    return Positioning.globalColumnRect(columnIndex, layoutConfig, scalingMetrics).pos;
  }

  public static globalWastePosition(
    layoutConfig: GameLayoutConfig,
    scalingMetrics: BoardScalingMetrics | undefined = undefined,
  ): PositionData {
    const scaleFactor = scalingMetrics?.scaleFactor ?? 1;
    const scaledOffsetX = scalingMetrics?.scaledOffsetX ?? 0;

    const xPos = layoutConfig.boardWidth * -0.25;
    const yPos = layoutConfig.boardHeight * 0.05;

    return { x: xPos * scaleFactor + scaledOffsetX, y: yPos * scaleFactor };
  }
}
